/**
 * ImpersonationModal
 *
 * Modal for super admins to select a user and impersonate them.
 * Search by name, email or store; supports owners, managers and staff.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { UserCog, Search } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function ImpersonationModal({ isOpen, onClose, onImpersonate }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const load = async () => {
      try {
        setLoading(true);
        const resp = await axios.get(`${API}/admin/impersonatable-users`);
        setUsers(resp.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.name, u.email, u.username, u.storeName, u.role]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [users, search]);

  const handleSelect = async (user) => {
    try {
      const resp = await axios.post(`${API}/admin/impersonate`, { user_id: user.id });
      const target = resp.data?.user || user;
      const store = resp.data?.store || null;
      onImpersonate({ ...target, storeName: user.storeName || store?.name }, store);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="impersonation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCog className="w-5 h-5" /> Impersonate User
          </DialogTitle>
        </DialogHeader>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            data-testid="impersonation-search"
            placeholder="Search by name, email or store…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="max-h-[60vh] overflow-y-auto border rounded-md divide-y" data-testid="impersonation-user-list">
          {loading ? (
            <div className="p-6 text-center text-gray-500">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No users found.</div>
          ) : (
            filtered.map((u) => (
              <div key={u.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                <div className="min-w-0">
                  <div className="font-medium truncate">{u.name || u.email || u.username || u.id}</div>
                  <div className="text-xs text-gray-500 truncate">
                    {u.email || u.username || '—'}
                    {u.storeName ? <span> · {u.storeName}</span> : null}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-3 shrink-0">
                  <Badge variant="outline" className="capitalize">{(u.role || '').replace('_', ' ')}</Badge>
                  <Button
                    size="sm"
                    onClick={() => handleSelect(u)}
                    data-testid={`impersonate-user-${u.id}`}
                  >
                    Impersonate
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ImpersonationModal;
